import { useMemo, useState } from 'react';
import { ChevronRight, PanelRightClose, PanelRightOpen } from 'lucide-react';
import type { TopologyConnection } from '@/hooks/useTopology';
import type { EdgeThroughput } from '@/hooks/useThroughputMetrics';
import { cn } from '@/lib/utils';
import { formatBits, formatCount } from './format';

/** Rows before the disclosure. Past this the reader is scanning, not comparing. */
const SHOWN = 8;

type Sort = 'lossiest' | 'busiest';

interface Row {
  id: string;
  label: string;
  bits: number;
  lossPerSecond: number;
  hasLoss: boolean;
}

/**
 * Which link in this view is worst.
 *
 * A whole-view comparison, which a panel about one link structurally cannot
 * answer — and which the canvas only gestures at: the width channel says "this
 * one is thicker than that one", the rail says by how much and in what order.
 * Always visible, no hover and no click required.
 *
 * It is also how a canvas graph gets a keyboard equivalent to hover. Every row
 * is focusable and selects the same edge a click on the canvas would, so the
 * values for links nine and beyond are not reachable only by pointing at a 2px
 * stroke.
 *
 * One hue throughout. Rank is magnitude, not identity, and colouring rows
 * categorically would burn the identity channel re-encoding bar length.
 */
export function TopTalkersRail({
  connections,
  throughput,
  selectedId,
  onSelect,
}: {
  connections: TopologyConnection[];
  throughput: Record<string, EdgeThroughput> | undefined;
  selectedId?: string;
  onSelect: (connectionId: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [sort, setSort] = useState<Sort | null>(null);

  const rows: Row[] = useMemo(() => {
    if (!throughput) return [];
    // A Map rather than the record itself: the project does not run
    // `noUncheckedIndexedAccess`, so an index lookup types as always present
    // while `Map.get` is honestly optional — and a connection the throughput
    // query returned nothing for is a real case. The rail lists what was
    // measured, not what exists.
    const measurements = new Map(Object.entries(throughput));
    return connections.flatMap((connection) => {
      const measured = measurements.get(connection.id);
      if (!measured) return [];
      const loss = measured.errors.errorsPerSec + measured.errors.discardsPerSec;
      return [{
        id: connection.id,
        label: `${connection.source.deviceName ?? connection.source.deviceIp} → ${connection.target.deviceName ?? connection.target.deviceIp}`,
        bits: Math.max(measured.inbound.current, measured.outbound.current),
        lossPerSecond: loss,
        hasLoss: measured.errors.hasErrors || loss > 0,
      }];
    });
  }, [connections, throughput]);

  // Lossiest whenever anything in scope is actually losing frames, busiest
  // otherwise. Busiest is a Tuesday-afternoon capacity question; this feature
  // exists for the other one. The reader can override, and their override
  // sticks — hence the null-means-not-yet-chosen state.
  const anyLoss = rows.some((row) => row.hasLoss);
  const effectiveSort: Sort = sort ?? (anyLoss ? 'lossiest' : 'busiest');

  // A sort the reader chose survives the data changing under it; a default
  // recomputed on every refetch would reorder the list while they read it.
  const [expandedFor, setExpandedFor] = useState(effectiveSort);
  if (effectiveSort !== expandedFor) {
    setExpandedFor(effectiveSort);
    setExpanded(false);
  }

  const ordered = useMemo(() => {
    const measure = (row: Row) => (effectiveSort === 'lossiest' ? row.lossPerSecond : row.bits);
    return [...rows].sort((a, b) => measure(b) - measure(a) || b.bits - a.bits);
  }, [rows, effectiveSort]);

  const visible = expanded ? ordered : ordered.slice(0, SHOWN);
  const hidden = ordered.length - visible.length;
  const scale = Math.max(
    1,
    ...ordered.map((row) => (effectiveSort === 'lossiest' ? row.lossPerSecond : row.bits)),
  );

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => { setCollapsed(false); }}
        className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 rounded-md border border-border bg-popover/95 px-2.5 py-1.5 text-xs text-popover-foreground shadow-md hover:text-foreground"
        aria-expanded={false}
      >
        <PanelRightOpen className="w-3.5 h-3.5" />
        {effectiveSort === 'lossiest' ? 'Lossiest links' : 'Busiest links'}
      </button>
    );
  }

  return (
    <aside
      /* Height fits its rows rather than the canvas: a rail holding eight links
         should be eight links tall, not a column of empty surface with the
         legend showing through underneath it. */
      className="absolute top-4 right-4 z-10 w-72 max-h-[calc(100%-2rem)] flex flex-col rounded-md border border-border bg-popover/95 text-popover-foreground shadow-md"
      aria-label="Links ranked"
    >
      <header className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle">
        {/* A sort control, not a filter: both orderings show every link. */}
        <div className="inline-flex rounded-md border border-border overflow-hidden" role="group" aria-label="Sort links">
          {(['lossiest', 'busiest'] as Sort[]).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => { setSort(option); }}
              aria-pressed={effectiveSort === option}
              className={cn(
                'px-2 py-0.5 text-[11px] border-r border-border last:border-r-0 capitalize',
                effectiveSort === option ? 'bg-muted text-foreground font-medium' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {option}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => { setCollapsed(true); }}
          className="ml-auto text-muted-foreground hover:text-foreground"
          aria-label="Collapse ranked links"
        >
          <PanelRightClose className="w-3.5 h-3.5" />
        </button>
      </header>

      {ordered.length === 0 ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">
          No link in this view has reported traffic in this range.
        </p>
      ) : (
        <>
          <ol className="flex-1 overflow-auto py-1">
            {visible.map((row) => {
              const measure = effectiveSort === 'lossiest' ? row.lossPerSecond : row.bits;
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => { onSelect(row.id); }}
                    aria-current={selectedId === row.id}
                    className={cn(
                      'w-full text-left px-3 py-1.5 hover:bg-accent focus:bg-accent focus:outline-none',
                      selectedId === row.id && 'bg-accent',
                    )}
                  >
                    <div className="flex items-baseline gap-2">
                      {/* Status rides a dot beside the label, never the bar's
                          colour — the bar is one hue because it encodes rank. */}
                      {row.hasLoss ? (
                        <span className="inline-block w-1.5 h-1.5 rounded-full shrink-0 bg-sev-1" aria-label="losing frames" />
                      ) : null}
                      <span className="text-xs truncate">{row.label}</span>
                      <span className="ml-auto text-[11px] text-muted-foreground tabular-nums shrink-0">
                        {effectiveSort === 'lossiest' ? formatCount(row.lossPerSecond) : formatBits(row.bits)}
                      </span>
                    </div>
                    <div className="mt-1 h-1 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-series-1"
                        style={{ width: `${String(Math.max(2, (measure / scale) * 100))}%`, backgroundColor: 'var(--series-1)' }}
                      />
                    </div>
                  </button>
                </li>
              );
            })}
          </ol>

          {hidden > 0 ? (
            <button
              type="button"
              onClick={() => { setExpanded(true); }}
              className="flex items-center gap-1 px-3 py-1.5 border-t border-border-subtle text-[11px] text-muted-foreground hover:text-foreground"
            >
              <ChevronRight className="w-3 h-3" />
              {hidden} more
            </button>
          ) : null}
        </>
      )}
    </aside>
  );
}
